import * as ClientFeatures from '@ip-only/client-features';
import { untypedIsEnabled } from '@ip-only/client-features';
import { environment } from '../environments/environment';
import { OfflineKeycloakService } from './keycloak/service/keycloak.service';
import { ContractorUserApiService } from './user/api/contractor-user-api.service';
import { ResourcesService } from './user/resources.service';
import { ParsedKeycloakToken, UserData } from './user/user';
import { UserService } from './user/user.service';

const USER_DATA_KEY = 'userData';

const getUserData = async (
  token: ParsedKeycloakToken,
  contractorUserApiService: ContractorUserApiService,
): Promise<UserData> => {
  const contractorData =
    await contractorUserApiService.fetchContractorUserData();
  const userData: UserData = {
    id: token.userScope,
    contractorScope: token.contractorScope,
    email: token.email,
    name: token.name,
    locale: token.locale,
    roles: token.realm_access?.roles ?? [],
    federatedUser: token.federatedUser,
    contractorData,
  };

  localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  return userData;
};

const getLocalUserData = (): UserData | null => {
  const userString = localStorage.getItem(USER_DATA_KEY);
  if (!userString) {
    return null;
  }
  return <UserData>JSON.parse(userString);
};

export const handleResourceClientFeatures = (
  resourceService: ResourcesService,
  resourceClientFeatures: { [key: string]: string[] },
) => {
  resourceService.getSelectedResource$().subscribe((resource) => {
    Object.keys(resourceClientFeatures).forEach((feature) => {
      const featureShouldBeActive =
        resource !== null &&
        resourceClientFeatures[feature].includes(resource.value);
      if (featureShouldBeActive === untypedIsEnabled(feature)) {
        return;
      }
      ClientFeatures.toggleFeature(feature);
    });
  });
};

export const loginFlow = (
  keycloakService: OfflineKeycloakService,
  userService: UserService,
  resourceService: ResourcesService,
  contractorUserService: ContractorUserApiService,
) =>
  // eslint-disable-next-line @typescript-eslint/no-misused-promises, no-async-promise-executor
  new Promise<void>(async (resolve, reject) => {
    const token = <ParsedKeycloakToken | undefined>(
      keycloakService.getParsedToken()
    );
    if (
      token &&
      !token.contractorScope &&
      !token.federatedUser &&
      token.authFlow !== 'contractor'
    ) {
      console.error('Incorrect token', token);
      return reject(new Error('Incorrect token'));
    }

    const userData = token
      ? await getUserData(token, contractorUserService)
      : getLocalUserData();

    if (!userData) {
      return reject(new Error('No user Data'));
    }
    userService.initializeUser(userData);
    userData.roles.length &&
      resourceService.initializeResources(userData.federatedUser);
    ClientFeatures.initClientFeatures(environment.clientFeatures);

    if (environment.resourceClientFeatures) {
      handleResourceClientFeatures(
        resourceService,
        environment.resourceClientFeatures,
      );
    }
    resolve();
  });
